/* Khurjeen theme — product page extras
   - <sale-countdown>  discount timer under the title
   - <live-counter>    social-proof numbers
   - <bundle-builder>  frequently bought together
*/

/* Sale countdown --------------------------------------------------------- */
class SaleCountdown extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-countdown-output]');
    this.duration = (parseInt(this.dataset.duration, 10) || 3600) * 1000;
    this.restart = this.dataset.restart === 'true';
    this.expiredText = this.dataset.expiredText || '';
    this.deadline = this.readDeadline();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /* The deadline is kept per visitor so the timer does not jump back to the
     full duration on every page view. */
  readDeadline() {
    const key = this.dataset.storageKey;
    let stored = null;
    try {
      stored = window.localStorage.getItem(key);
    } catch (e) {
      stored = null;
    }

    const now = Date.now();
    if (stored) {
      const value = parseInt(stored, 10);
      if (value > now) return value;
      if (!this.restart) return value;
    }

    const fresh = now + this.duration;
    try {
      window.localStorage.setItem(key, String(fresh));
    } catch (e) {
      /* private mode — the timer still runs, it just restarts on reload */
    }
    return fresh;
  }

  tick() {
    if (!this.output) return;
    let remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      if (this.restart) {
        this.deadline = this.readDeadline();
        remaining = this.deadline - Date.now();
      } else {
        this.output.textContent = this.expiredText;
        clearInterval(this.timer);
        return;
      }
    }

    const total = Math.floor(remaining / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    this.output.textContent = `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }
}
customElements.define('sale-countdown', SaleCountdown);

/* Social-proof counter --------------------------------------------------- */
class LiveCounter extends HTMLElement {
  connectedCallback() {
    this.min = parseInt(this.dataset.min, 10) || 1;
    this.max = parseInt(this.dataset.max, 10) || this.min + 1;
    this.suffix = this.dataset.suffix || '';
    this.value = parseInt(this.dataset.start, 10) || this.min;

    const interval = parseInt(this.dataset.interval, 10) || 0;
    if (interval > 0) {
      this.timer = setInterval(() => this.drift(), interval);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /* Nudge the number by a small step rather than jumping randomly, so it
     reads like a live figure instead of noise. */
  drift() {
    const step = Math.floor(Math.random() * 3) + 1;
    const direction = Math.random() > 0.5 ? 1 : -1;
    let next = this.value + step * direction;
    if (next < this.min) next = this.min + step;
    if (next > this.max) next = this.max - step;
    this.value = next;
    this.textContent = `${next}${this.suffix}`;
  }
}
customElements.define('live-counter', LiveCounter);

/* Frequently bought together -------------------------------------------- */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.checkboxes = Array.from(this.querySelectorAll('[data-fbt-item]'));
    this.totalOutput = this.querySelector('[data-fbt-total]');
    this.submitButton = this.querySelector('[data-fbt-submit]');
    this.spinner = this.querySelector('[data-fbt-spinner]');
    this.errorOutput = this.querySelector('[data-fbt-error]');
    this.moneyFormat =
      this.dataset.moneyFormat || (window.shopSettings && window.shopSettings.moneyFormat);

    this.checkboxes.forEach((box) => {
      if (box.hasAttribute('data-locked')) {
        /* Keep the current product in the bundle without disabling the input,
           so its value still reads as checked. */
        box.addEventListener('click', (event) => event.preventDefault());
      }
      box.addEventListener('change', () => this.updateTotal());
    });

    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.addSelected());
    }

    /* Keep the bundle in step when the shopper picks a different variant */
    this.onVariantChange = (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      const mainBox = this.checkboxes.find((box) => box.hasAttribute('data-main-product'));
      if (mainBox) {
        mainBox.dataset.variantId = variant.id;
        mainBox.dataset.price = variant.price;
      }
      this.dataset.mainVariant = variant.id;
      const row = this.querySelector('.fbt__row .fbt__row-price');
      if (row) row.textContent = formatMoney(variant.price, this.moneyFormat);
      const preview = this.querySelector('.fbt__preview-item .fbt__preview-price');
      if (preview) preview.textContent = formatMoney(variant.price, this.moneyFormat);
      this.updateTotal();
    };
    document.addEventListener('variant:change', this.onVariantChange);

    this.updateTotal();
  }

  /* The main product's variant can change after the shopper picks options,
     so read it live from the product form instead of trusting the markup. */
  currentMainVariant() {
    const form = document.querySelector(
      `[data-product-form="${this.dataset.sectionId}"] [name="id"], .product-form form [name="id"]`
    );
    return form && form.value ? form.value : this.dataset.mainVariant;
  }

  selected() {
    return this.checkboxes.filter((box) => box.checked);
  }

  updateTotal() {
    const cents = this.selected().reduce(
      (sum, box) => sum + (parseInt(box.dataset.price, 10) || 0),
      0
    );
    this.querySelectorAll('[data-fbt-preview]').forEach((card) => {
      const match = this.checkboxes.find(
        (box) => box.dataset.variantId === card.dataset.fbtPreview
      );
      card.classList.toggle('is-deselected', !!match && !match.checked);
    });
    if (this.totalOutput) {
      this.totalOutput.textContent = formatMoney(cents, this.moneyFormat);
    }
    if (this.submitButton) {
      this.submitButton.disabled = this.selected().length === 0;
    }
  }

  addSelected() {
    const chosen = this.selected();
    if (chosen.length === 0) return;

    const mainVariant = this.currentMainVariant();
    const items = chosen.map((box) => ({
      id: Number(box.hasAttribute('data-main-product') ? mainVariant : box.dataset.variantId),
      quantity: 1
    }));

    this.setLoading(true);
    this.hideError();

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status !== 200) {
          this.showError(response.description || response.message);
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer && window.shopSettings.cartType === 'drawer' && response.sections) {
          drawer.renderContents(response);
        } else {
          window.location.href = window.routes.cart_url;
        }
      })
      .catch(() => this.showError(window.cartStrings.error))
      .finally(() => this.setLoading(false));
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  setLoading(state) {
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-disabled', state);
    this.submitButton.classList.toggle('loading', state);
    if (this.spinner) this.spinner.classList.toggle('hidden', !state);
  }

  showError(message) {
    if (!this.errorOutput) return;
    this.errorOutput.textContent = message;
    this.errorOutput.removeAttribute('hidden');
  }

  hideError() {
    if (this.errorOutput) this.errorOutput.setAttribute('hidden', '');
  }
}
customElements.define('bundle-builder', BundleBuilder);
